// ============================================
// GAME OVER SCREEN - ゲームオーバ�E画面の描画とリザルト表示
// ============================================

import { getP5 } from '../../core/p5-instance';
import { CONFIG, RankConfig } from '../../config/game-config';
import {
    getGameOverTimer,
    setGameOverTimer,
    getScore,
    getHighScore,
    getWave,
    getMaxCombo,
    getEnemiesDefeated,
    getDefeatedByType,
    getResultPhase,
    setResultPhase,
    getResultDisplayScore,
    setResultDisplayScore,
    getResultEnemyIndex,
    setResultEnemyIndex,
    getResultRankShown,
    setResultRankShown,
    getResultParticles,
    setResultParticles,
    getCanRestart,
    setCanRestart
} from '../../store/index';
import type { EnemyType } from '../../types/game-types';

// レイアウト定数
const PANEL_MARGIN_X = 25;
const TEXT_PADDING_X = 45;

// ============================================
// drawGameOver() - ゲームオーバー画面の描画（リザルト付き）
// ============================================
export function drawGameOver() {
    const p = getP5();
    // ゲームオーバータイマー更新
    setGameOverTimer(getGameOverTimer() + 1);

    // 半透明の黒いオーバーレイ
    p.fill(0, 0, 0, 220);
    p.rect(0, 0, p.width, p.height);

    p.textAlign(p.CENTER, p.CENTER);
    p.textFont('Orbitron');

    // ランク取得（事前計算）
    const rank = calculateRank();
    // リザルトパーティクル更新
    updateResultParticles();
    drawResultParticles();

    // 「GAME OVER」のグロー効果
    for (let i = 3; i > 0; i--) {
        p.fill(255, 0, 100, 50);
        p.textSize(36 + i * 3);
        p.text('GAME OVER', p.width / 2, 45);
    }
    p.fill(255, 0, 100);
    p.textSize(36);
    p.text('GAME OVER', p.width / 2, 45);

    // --- フェーズ0: スコアカウントアップ ---
    const scoreY = 85;
    const score = getScore();
    let resultPhase = getResultPhase();
    let resultDisplayScore = getResultDisplayScore();

    // スコアカウントアップアニメーション
    if (resultPhase === 0) {
        const countSpeed = p.max(1, p.floor(score / 120)); // 2秒でカウント完了
        resultDisplayScore = p.min(resultDisplayScore + countSpeed, score);
        setResultDisplayScore(resultDisplayScore);

        if (resultDisplayScore >= score) {
            setResultDisplayScore(score);
            setResultPhase(1);
            // スコア確定時にバーストエフェクト
            createScoreBurstEffect();
        }
    }

    // スコア表示：確定時はパルス
    let scorePulse = (resultPhase > 0 && getGameOverTimer() % 60 < 30) ? 1.1 : 1.0;
    if (resultPhase === 0) {
        scorePulse = 1.0;
    }

    // スコアラベル
    p.textAlign(p.CENTER, p.CENTER);
    p.fill(200);
    p.textSize(12);
    p.text('FINAL SCORE', p.width / 2, scoreY - 15);

    // スコア数値：等幅フォント風に個別描画
    const scoreStr = p.floor(resultDisplayScore).toString().padStart(8, '0');
    const digitWidth = 22 * scorePulse;
    const scoreStartX = p.width / 2 - (digitWidth * 8) / 2 + digitWidth / 2;

    p.textSize(26 * scorePulse);
    for (let i = 0; i < scoreStr.length; i++) {
        // 先頭のゼロは薄く
        if (i < scoreStr.length - p.floor(resultDisplayScore).toString().length && resultDisplayScore > 0) {
            p.fill(0, 150, 150);
        } else {
            p.fill(0, 255, 255);
        }
        p.text(scoreStr[i], scoreStartX + i * digitWidth, scoreY + 5);
    }

    // ハイスコア更新表示
    const highScore = getHighScore();
    if (resultPhase > 0 && score >= highScore && highScore > 0) {
        const pulse = p.sin(p.frameCount * 0.15) * 0.3 + 0.7;
        p.fill(255, 215, 0, 255 * pulse);
        p.textSize(12);
        p.text('★ NEW HIGH SCORE ★', p.width / 2, scoreY + 30);
    }

    // --- リザルトパネル背景 ---
    const panelY = 150;
    const panelHeight = 260;
    p.fill(20, 20, 40, 200);
    p.stroke(0, 255, 255, 100);
    p.strokeWeight(2);
    p.rect(PANEL_MARGIN_X, panelY, p.width - PANEL_MARGIN_X * 2, panelHeight, 10);
    p.noStroke();

    // --- フェーズ1: 統計情報を順番に表示 ---
    let statsY = panelY + 22;
    const lineHeight = 28;
    const enemyTypes = ['normal', 'tank', 'fast', 'armored', 'speedy', 'laser', 'mirror', 'boss'];
    const enemyNames = ['NORMAL', 'TANK', 'FAST', 'ARMORED', 'SPEEDY', 'LASER', 'MIRROR', 'BOSS'];
    let resultEnemyIndex = getResultEnemyIndex();

    // フェーズ1でインデックスを進める
    if (resultPhase === 1) {
        if (getGameOverTimer() % 8 === 0 && resultEnemyIndex < enemyTypes.length + 3) {
            resultEnemyIndex++;
            setResultEnemyIndex(resultEnemyIndex);
            // 表示時にサウンド的なエフェクト
            if (resultEnemyIndex <= enemyTypes.length + 2) {
                createStatRevealEffect(resultEnemyIndex);
            }
        }
        if (resultEnemyIndex >= enemyTypes.length + 3) {
            setResultPhase(2);
            resultPhase = 2;
        }
    }

    // 到達ウェーブ（インデックス0で表示）
    const wave = getWave();
    if (resultEnemyIndex >= 1 || resultPhase >= 2) {
        p.textAlign(p.LEFT, p.CENTER);
        p.fill(200);
        p.textSize(12);
        p.text('WAVE REACHED', TEXT_PADDING_X, statsY);
        p.textAlign(p.RIGHT, p.CENTER);
        p.fill(255, 0, 255);
        p.textSize(16);
        p.text(wave, p.width - TEXT_PADDING_X, statsY);
    }

    // 最大コンボ（インデックス1で表示）
    statsY += lineHeight;
    const maxCombo = getMaxCombo();
    if (resultEnemyIndex >= 2 || resultPhase >= 2) {
        p.textAlign(p.LEFT, p.CENTER);
        p.fill(200);
        p.textSize(12);
        p.text('MAX COMBO', TEXT_PADDING_X, statsY);
        p.textAlign(p.RIGHT, p.CENTER);
        p.fill(255, 215, 0);
        p.textSize(16);
        p.text(maxCombo, p.width - TEXT_PADDING_X, statsY);
    }

    // 総撃破数（インデックス2で表示）
    statsY += lineHeight;
    const enemiesDefeated = getEnemiesDefeated();
    if (resultEnemyIndex >= 3 || resultPhase >= 2) {
        p.textAlign(p.LEFT, p.CENTER);
        p.fill(200);
        p.textSize(12);
        p.text('TOTAL DEFEATED', TEXT_PADDING_X, statsY);
        p.textAlign(p.RIGHT, p.CENTER);
        p.fill(0, 255, 100);
        p.textSize(16);
        p.text(enemiesDefeated, p.width - TEXT_PADDING_X, statsY);
    }

    // 区切り線
    statsY += lineHeight - 8;
    if (resultEnemyIndex >= 3 || resultPhase >= 2) {
        p.stroke(100);
        p.strokeWeight(1);
        p.line(TEXT_PADDING_X, statsY, p.width - TEXT_PADDING_X, statsY);
        p.noStroke();
    }
    statsY += 15;

    // 敵タイプ別撃破数：2列表示、8種類対応：左4, 右4
    const col1X = TEXT_PADDING_X;
    const col2X = p.width / 2 + 5;
    const iconSize = 12;
    const rowH = 21;
    const defeatedByType = getDefeatedByType();

    for (let i = 0; i < enemyTypes.length; i++) {
        const enemyType = enemyTypes[i] as EnemyType;
        const showIndex = i + 4; // 基本統計の後
        if (resultEnemyIndex >= showIndex || resultPhase >= 2) {
            // 左列に4つ、右列に4つ
            const colX = i < 4 ? col1X : col2X;
            const rowIndex = i < 4 ? i : i - 4;
            const yPos = statsY + rowIndex * rowH;

            drawMiniEnemyIcon(colX + 6, yPos, enemyType, iconSize);
            p.textAlign(p.LEFT, p.CENTER);
            p.fill(255);
            p.textSize(10);
            p.text(enemyNames[i], colX + 18, yPos);
            p.textAlign(p.RIGHT, p.CENTER);
            p.text(defeatedByType[enemyType], colX + 100, yPos);
        }
    }

    // --- フェーズ2: ランク表示（エフェクト付き）---
    const rankY = 480;
    const resultRankShown = getResultRankShown();
    if (resultPhase >= 2) {
        if (!resultRankShown) {
            setResultRankShown(true);
            createRankRevealEffect(rank);
        }

        // tier 3以上（A〜SSS）の背景エフェクト
        if (rank.tier >= 3) {
            drawRankBackground(rank, rankY);
        }

        // ランク表示アニメーション
        const rankTimer = getGameOverTimer() - 60 - (enemyTypes.length + 3) * 8;
        const bouncePeak = 1.1 + rank.tier * 0.05;
        const idlePulse = 0.02 + rank.tier * 0.01;
        let rankScale = rankTimer < 15
            ? (rankTimer / 15) * bouncePeak
            : 1.0 + p.sin(rankTimer * 0.1) * idlePulse;
        rankScale = p.max(0, rankScale);

        // ランクテキストのサイズを動的に計算
        const rankTextSize = 36 * rankScale;

        // ランク背景円（全ランク共通、alphaがtierでスケール）
        const circleY = rankY;
        const fillAlpha = 20 + rank.tier * 5;
        const strokeAlpha = 80 + rank.tier * 15;
        p.fill(rank.color[0], rank.color[1], rank.color[2], fillAlpha);
        p.stroke(rank.color[0], rank.color[1], rank.color[2], strokeAlpha);
        p.strokeWeight(2);
        p.circle(p.width / 2, circleY, 50 * rankScale);

        // tier 3以上（A〜SSS）はリング追加
        const ringCount = Math.max(0, rank.tier - 2); // A=1, S=2, SS=3, SSS=4
        for (let r = ringCount; r > 0; r--) {
            const ringSize = (50 + r * 12) * rankScale;
            const ringAlpha = 150 - r * 30;
            p.noFill();
            p.stroke(rank.color[0], rank.color[1], rank.color[2], ringAlpha);
            p.strokeWeight(2);
            p.circle(p.width / 2, circleY, ringSize);
        }

        // RANKラベル：ランクテキストの上
        p.textAlign(p.CENTER, p.BOTTOM);
        p.fill(150);
        p.noStroke();
        p.textSize(10);
        p.text('RANK', p.width / 2, rankY - 25 * rankScale);

        // ランクテキスト（tier 1以上でCanvas 2Dグロー付き）
        p.textAlign(p.CENTER, p.CENTER);
        if (rank.tier >= 1) {
            const ctx = p.drawingContext as CanvasRenderingContext2D;
            ctx.save();
            ctx.shadowColor = `rgba(${rank.color[0]}, ${rank.color[1]}, ${rank.color[2]}, 0.8)`;
            ctx.shadowBlur = rank.tier * 4;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
            p.fill(rank.color[0], rank.color[1], rank.color[2]);
            p.textSize(rankTextSize);
            p.text(rank.name, p.width / 2, rankY);
            ctx.shadowBlur = 0;
            ctx.restore();
        } else {
            p.fill(rank.color[0], rank.color[1], rank.color[2]);
            p.textSize(rankTextSize);
            p.text(rank.name, p.width / 2, rankY);
        }

        // 1.5秒後にリスタート可能
        if (rankTimer > 45) {
            setCanRestart(true);
        }
    }

    // --- リスタートボタン ---
    const btnY = 545;
    const btnW = 160;
    const btnH = 45;
    const btnX = p.width / 2 - btnW / 2;
    const canRestart = getCanRestart();

    if (canRestart) {
        const pulse = p.sin(p.frameCount * 0.08) * 0.2 + 0.8;
        p.fill(0, 200, 255, 200 * pulse);
        p.stroke(0, 255, 255);
        p.strokeWeight(3);
        p.rect(btnX, btnY, btnW, btnH, 8);

        p.fill(255);
        p.noStroke();
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(18);
        p.text('RETRY', p.width / 2, btnY + btnH / 2);
    } else {
        p.fill(50, 50, 50, 200);
        p.stroke(100);
        p.strokeWeight(2);
        p.rect(btnX, btnY, btnW, btnH, 8);

        p.fill(100);
        p.noStroke();
        p.textAlign(p.CENTER, p.CENTER);
        p.textSize(14);
        p.text('...', p.width / 2, btnY + btnH / 2);
    }
}

// ============================================
// createScoreBurstEffect() - スコア確定時のバーストエフェクト
// ============================================
export function createScoreBurstEffect() {
    const p = getP5();
    const particles = getResultParticles();
    for (let i = 0; i < 20; i++) {
        const angle = (p.TWO_PI / 20) * i;
        particles.push({
            x: p.width / 2,
            y: 100, // スコア表示位置に合わせて調整
            vx: p.cos(angle) * p.random(3, 8),
            vy: p.sin(angle) * p.random(3, 8),
            size: p.random(4, 10),
            color: p.color(0, 255, 255, 200),
            life: p.random(20, 40),
            type: 'burst'
        });
    }
    setResultParticles(particles);
}

// ============================================
// createStatRevealEffect() - 統計表示時のエフェクト
// ============================================
export function createStatRevealEffect(index: number) {
    const p = getP5();
    const particles = getResultParticles();
    const y = 155 + (index - 1) * 26;
    for (let i = 0; i < 5; i++) {
        particles.push({
            x: TEXT_PADDING_X + p.random(p.width - TEXT_PADDING_X * 2),
            y: y,
            vx: p.random(-1, 1),
            vy: p.random(-2, -1),
            size: p.random(2, 5),
            color: p.color(255, 255, 255, 150),
            life: p.random(15, 25),
            type: 'stat'
        });
    }
    setResultParticles(particles);
}

// ============================================
// createRankRevealEffect() - ランク表示時のエフェクト
// ============================================
export function createRankRevealEffect(rank: RankConfig) {
    const p = getP5();
    const particles = getResultParticles();
    const particleCounts = [8, 12, 18, 25, 35, 50, 60];
    const particleCount = particleCounts[rank.tier];

    const minSpeed = 2 + rank.tier * 0.5;
    const maxSpeed = 6 + rank.tier * 1.5;

    for (let i = 0; i < particleCount; i++) {
        const angle = (p.TWO_PI / particleCount) * i;
        const speed = p.random(minSpeed, maxSpeed);
        particles.push({
            x: p.width / 2,
            y: 450,
            vx: p.cos(angle) * speed,
            vy: p.sin(angle) * speed,
            size: p.random(5, 12),
            color: p.color(rank.color[0], rank.color[1], rank.color[2], 220),
            life: p.random(30, 60),
            type: 'rank',
            sparkle: rank.tier >= 4
        });
    }

    // tier 5以上（SS/SSS）: ボーナススパークル
    if (rank.tier >= 5) {
        const sparkleCount = rank.tier >= 6 ? 30 : 15;
        for (let i = 0; i < sparkleCount; i++) {
            particles.push({
                x: p.width / 2 + p.random(-100, 100),
                y: 450 + p.random(-50, 50),
                vx: p.random(-2, 2),
                vy: p.random(-3, 0),
                size: p.random(2, 6),
                color: p.color(255, 255, 200, 255),
                life: p.random(40, 80),
                type: 'sparkle'
            });
        }
    }
    setResultParticles(particles);
}

// ============================================
// drawRankBackground() - ランク背景エフェクト（tier 3以上）
// ============================================
export function drawRankBackground(rank: RankConfig, rankY: number) {
    const p = getP5();
    const gameOverTimer = getGameOverTimer();

    // 放射状の光線: (tier - 2) * 4 → A=4, S=8, SS=12, SSS=16
    p.push();
    p.translate(p.width / 2, rankY);
    const rayCount = (rank.tier - 2) * 4;
    const rayLen = 40 + (rank.tier - 3) * 30; // A=40, S=70, SS=100, SSS=130
    for (let i = 0; i < rayCount; i++) {
        const angle = (p.TWO_PI / rayCount) * i + gameOverTimer * 0.015;
        const rayAlpha = p.sin(gameOverTimer * 0.08 + i) * 40 + 50;
        p.stroke(rank.color[0], rank.color[1], rank.color[2], rayAlpha);
        p.strokeWeight(rank.tier >= 6 ? 3 : 2);
        p.line(0, 0, p.cos(angle) * rayLen, p.sin(angle) * rayLen);
    }
    p.pop();

    // tier 5以上（SS/SSS）: オーラエフェクト
    if (rank.tier >= 5) {
        const auraSize = 80 + p.sin(gameOverTimer * 0.05) * 10;
        const auraAlpha = 30 + p.sin(gameOverTimer * 0.1) * 20;
        p.noStroke();
        for (let i = 3; i > 0; i--) {
            p.fill(rank.color[0], rank.color[1], rank.color[2], auraAlpha / i);
            p.circle(p.width / 2, rankY, auraSize + i * 20);
        }
    }

    // tier 6（SSS）: 回転する星
    if (rank.tier >= 6) {
        for (let i = 0; i < 8; i++) {
            const starAngle = (p.TWO_PI / 8) * i + gameOverTimer * 0.03;
            const starDist = 60 + p.sin(gameOverTimer * 0.1 + i) * 8;
            const starX = p.width / 2 + p.cos(starAngle) * starDist;
            const starY = rankY + p.sin(starAngle) * starDist;
            const starAlpha = 150 + p.sin(gameOverTimer * 0.2 + i * 0.5) * 100;
            p.fill(255, 255, 200, starAlpha);
            p.noStroke();
            drawStar4(starX, starY, 6);
        }
    }
}

// ============================================
// drawStar4() - 4点星描画ヘルパー
// ============================================
export function drawStar4(x: number, y: number, size: number) {
    const p = getP5();
    p.beginShape();
    for (let i = 0; i < 8; i++) {
        const angle = (p.TWO_PI / 8) * i - p.HALF_PI;
        const r = i % 2 === 0 ? size : size * 0.4;
        p.vertex(x + p.cos(angle) * r, y + p.sin(angle) * r);
    }
    p.endShape(p.CLOSE);
}

// ============================================
// updateResultParticles() - リザルトパーティクル更新
// ============================================
export function updateResultParticles() {
    const particles = getResultParticles();
    for (let i = particles.length - 1; i >= 0; i--) {
        const particle = particles[i];
        particle.x += particle.vx;
        particle.y += particle.vy;
        if (particle.type === 'sparkle') {
            particle.vy += 0.02; // 軽い重力
        } else {
            particle.vx *= 0.96;
            particle.vy *= 0.96;
        }
        particle.life--;
        if (particle.life <= 0) {
            particles.splice(i, 1);
        }
    }
    setResultParticles(particles);
}

// ============================================
// drawResultParticles() - リザルトパーティクル描画
// ============================================
export function drawResultParticles() {
    const p = getP5();
    const particles = getResultParticles();
    p.noStroke();
    for (const particle of particles) {
        const alpha = (particle.life / 60) * 255;
        if (particle.type === 'sparkle') {
            p.fill(p.red(particle.color), p.green(particle.color), p.blue(particle.color), alpha);
            drawStar4(particle.x, particle.y, particle.size);
        } else {
            p.fill(p.red(particle.color), p.green(particle.color), p.blue(particle.color), alpha);
            p.circle(particle.x, particle.y, particle.size * (particle.life / 40));
        }
    }
}

// ============================================
// isRetryButtonClicked() - リトライボタンがクリックされたか判定
// ============================================
export function isRetryButtonClicked() {
    const p = getP5();
    const btnY = 545;
    const btnW = 160;
    const btnH = 45;
    const btnX = p.width / 2 - btnW / 2;

    return p.mouseX >= btnX && p.mouseX <= btnX + btnW &&
           p.mouseY >= btnY && p.mouseY <= btnY + btnH;
}

// ============================================
// calculateRank() - スコアとウェーブからランクを計算
// ============================================
export function calculateRank() {
    const score = getScore();
    const wave = getWave();
    for (const rank of CONFIG.ranks) {
        if (score >= rank.minScore && wave >= rank.minWave) {
            return rank;
        }
    }
    return CONFIG.ranks[CONFIG.ranks.length - 1];
}

// ============================================
// drawMiniEnemyIcon() - リザルト用の小さな敵アイコン
// ============================================
export function drawMiniEnemyIcon(x: number, y: number, type: EnemyType, size: number) {
    const p = getP5();
    p.push();
    p.translate(x, y);
    p.noStroke();

    if (type === 'normal') {
        p.fill(255, 50, 100);
        p.beginShape();
        p.vertex(0, -size / 2);
        p.vertex(size / 2, 0);
        p.vertex(0, size / 2);
        p.vertex(-size / 2, 0);
        p.endShape(p.CLOSE);
    } else if (type === 'tank') {
        p.fill(255, 150, 50);
        p.beginShape();
        for (let i = 0; i < 6; i++) {
            const angle = p.TWO_PI / 6 * i - p.HALF_PI;
            p.vertex(p.cos(angle) * size / 2, p.sin(angle) * size / 2);
        }
        p.endShape(p.CLOSE);
    } else if (type === 'fast') {
        p.fill(150, 50, 255);
        p.triangle(0, -size / 2, -size / 2, size / 2, size / 2, size / 2);
    } else if (type === 'armored') {
        p.fill(180, 180, 200);
        p.beginShape();
        for (let i = 0; i < 8; i++) {
            const angle = p.TWO_PI / 8 * i - p.HALF_PI;
            p.vertex(p.cos(angle) * size / 2, p.sin(angle) * size / 2);
        }
        p.endShape(p.CLOSE);
    } else if (type === 'speedy') {
        p.fill(0, 255, 200);
        p.beginShape();
        p.vertex(0, -size / 2);
        p.vertex(size / 3, 0);
        p.vertex(0, size / 2);
        p.vertex(-size / 3, 0);
        p.endShape(p.CLOSE);
    } else if (type === 'laser') {
        p.fill(255, 0, 100);
        // 六角形
        p.beginShape();
        for (let i = 0; i < 6; i++) {
            const angle = p.TWO_PI / 6 * i - p.HALF_PI;
            p.vertex(p.cos(angle) * size / 2, p.sin(angle) * size / 2);
        }
        p.endShape(p.CLOSE);
        // 砲身
        p.fill(100, 50, 80);
        p.rect(-size / 6, size / 4, size / 3, size / 3);
    } else if (type === 'mirror') {
        p.fill(150, 200, 255);
        // 八角形
        p.beginShape();
        for (let i = 0; i < 8; i++) {
            const angle = p.TWO_PI / 8 * i - p.PI / 8;
            p.vertex(p.cos(angle) * size / 2, p.sin(angle) * size / 2);
        }
        p.endShape(p.CLOSE);
        // 反封E�Eーク
        p.fill(255, 255, 255, 180);
        p.circle(0, 0, size / 4);
    } else if (type === 'boss') {
        p.fill(255, 0, 0);
        p.beginShape();
        for (let i = 0; i < 10; i++) {
            const angle = p.TWO_PI / 10 * i - p.HALF_PI;
            const r = i % 2 === 0 ? size / 2 : size / 3;
            p.vertex(p.cos(angle) * r, p.sin(angle) * r);
        }
        p.endShape(p.CLOSE);
    }

    p.pop();
}
